import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const eventId = searchParams.get('eventId');
  const inviteToken = searchParams.get('inviteToken');
  const redirectPath = searchParams.get('redirect') || '/events';

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      if (inviteToken && eventId && user) {
        console.log('[SetPassword] Checking for invitation:', { inviteToken, eventId, userId: user.id });

        const { data: invitation, error: inviteError } = await supabase
          .from('event_invitations')
          .select('role')
          .eq('token', inviteToken)
          .eq('event_id', eventId)
          .eq('status', 'pending')
          .maybeSingle();

        console.log('[SetPassword] Invitation query result:', { invitation, inviteError });

        if (invitation) {
          console.log('[SetPassword] Granting event access...');

          const { error: accessError } = await supabase
            .from('event_access')
            .insert({
              event_id: eventId,
              user_id: user.id,
              role: invitation.role,
            });

          if (accessError && !accessError.message?.includes('duplicate')) {
            console.error('[SetPassword] Error granting access:', accessError);
          } else {
            console.log('[SetPassword] Access granted successfully');
          }

          await supabase
            .from('event_invitations')
            .update({ status: 'accepted' })
            .eq('token', inviteToken)
            .eq('event_id', eventId);
        } else {
          console.warn('[SetPassword] No pending invitation found - checking if user already has access...');

          const { data: existingAccess } = await supabase
            .from('event_access')
            .select('id')
            .eq('event_id', eventId)
            .eq('user_id', user.id)
            .maybeSingle();

          console.log('[SetPassword] Existing access check:', existingAccess ? 'User already has access' : 'No access found');
        }

        console.log('[SetPassword] Redirecting to portal...');
        navigate(`/portal?eventId=${eventId}&section=overview`);
        return;
      }

      const destination = eventId
        ? `/portal?eventId=${eventId}&section=overview`
        : redirectPath;

      navigate(destination);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/slidepusher_events.svg"
            alt="Slidepusher Events"
            className="h-20 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-white mb-2">Set Your Password</h1>
          <p className="text-zinc-400">
            Welcome! Please set a password for your account to continue.
          </p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white pr-10"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-zinc-500">Must be at least 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white pr-10"
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white"
            >
              {loading ? 'Setting Password...' : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
